import { FC, memo, useMemo } from 'react';
import { Input, Select } from 'antd';
import styles from './style.module.css';
import { Clinic } from '../../../../models/MedVox/Clinic';
import { OptionLike } from '../../../../hooks/shared/useFilterOption';
import { Role } from '../../../../models/Users/Role';
import { ROLES } from '../../constants';
import { ClinicSelect } from '../../../../components/shared/ClinicSelect';

interface Props {
  roles: Role[];
  clinics: Clinic[];
  clinicValue: string[];
  roleValue: string[];
  isLockoutValue: string;
  handleClinicsChange: (value: string[]) => void;
  handleOpenClinics: (visible: boolean) => void;
  handleRoleChange: (value: string[]) => void;
  handleOpenRoles: (visible: boolean) => void;
  handleIsLockoutChange: (value: string) => void;
  handleOpenIsLockout: (visible: boolean) => void;
  onSearch: (e: React.ChangeEvent<HTMLInputElement>) => void;
  filterOption: (
    inputValue: string,
    option?: OptionLike | undefined,
  ) => boolean;
  disabled?: boolean;
}

export const UsersTableFilter: FC<Props> = memo(
  ({
    clinics,
    roles,
    handleClinicsChange,
    clinicValue,
    handleOpenClinics,
    handleRoleChange,
    roleValue,
    handleOpenRoles,
    handleIsLockoutChange,
    isLockoutValue,
    handleOpenIsLockout,
    onSearch,
    filterOption,
    disabled = false,
  }: Props) => {
    const rolesArray = useMemo(() => {
      const selected = roles.filter((role) => roleValue.includes(role.id));
      const nonSelected = roles.filter((role) => !roleValue.includes(role.id));

      return [...selected, ...nonSelected].map((role) => {
        return {
          value: role.id,
          id: role.id,
          label: ROLES[role.name],
        };
      });
    }, [roleValue, roles]);

    const isLockoutArray = useMemo(() => {
      const values = [
        {
          value: 'all',
          id: 'all',
          label: 'Все',
        },
        {
          value: 'false',
          id: 'false',
          label: 'Активен',
        },
        {
          value: 'true',
          id: 'true',
          label: 'Заблокирован',
        },
      ];
      const selected = values.filter((value) => isLockoutValue === value.id);
      const nonSelected = values.filter((value) => isLockoutValue !== value.id);

      return [...selected, ...nonSelected].map((value) => {
        return {
          value: value.id,
          id: value.id,
          label: value.label,
        };
      });
    }, [isLockoutValue]);

    return (
      <div className={styles.filterBlock}>
        <div className={styles.inputsContainer}>
          <label className={styles.label}>Логин</label>
          <Input
            allowClear
            className={styles.search}
            placeholder="Поиск по логину"
            onChange={onSearch}
            style={{ fontSize: 14 }}
            disabled={disabled}
          />
        </div>
        <div className={styles.inputsContainer}>
          <label className={styles.label}>Роль</label>
          <Select
            allowClear
            className={styles.input}
            placeholder="Все"
            mode="multiple"
            value={roleValue}
            filterOption={filterOption}
            onChange={handleRoleChange}
            showSearch={true}
            onOpenChange={handleOpenRoles}
            removeIcon={null}
            maxTagCount="responsive"
            maxTagTextLength={24}
            options={rolesArray}
            autoClearSearchValue={false}
            disabled={disabled}
            optionRender={(state) => (
              <span className={styles.inputSpan}> {state.label}</span>
            )}
          ></Select>
        </div>
        <div className={styles.inputsContainer}>
          <label className={styles.label}>Медицинская организация</label>
          <ClinicSelect
            clinics={clinics}
            value={clinicValue}
            onChange={handleClinicsChange}
            className={styles.input}
            placeholder="Все"
            filterOption={filterOption}
            onOpenChange={handleOpenClinics}
            disabled={disabled}
            optionRender={(state) => (
              <span className={styles.inputSpan}> {state.label}</span>
            )}
          />
        </div>
        <div className={styles.inputsContainer}>
          <label className={styles.label}>Статус</label>
          <Select
            allowClear
            className={styles.input}
            placeholder="Все"
            value={isLockoutValue}
            filterOption={filterOption}
            onChange={handleIsLockoutChange}
            showSearch={true}
            onOpenChange={handleOpenIsLockout}
            removeIcon={null}
            maxTagCount="responsive"
            maxTagTextLength={24}
            options={isLockoutArray}
            autoClearSearchValue={false}
            disabled={disabled}
            optionRender={(state) => (
              <span className={styles.inputSpan}> {state.label}</span>
            )}
          ></Select>
        </div>
      </div>
    );
  },
);

UsersTableFilter.displayName = 'UsersTableFilter';
